import _ from 'lodash';
import CloneUtils from '../../../../utils/cloneUtils';
import {
    ROW_MAPPING_TARGET,
    ROW_MAPPING_TYPE,
    TEMPORARY_DESCRIPTOR_ID_PREFIX,
    TEMPORARY_ROW_ID_PREFIX,
} from '../constants/generalConstants';
import {
    CellsBoundingBoxesMapType,
    MappingsMapType,
    RowIdToDescriptorIdConfidenceMapType,
    RowMappingType,
    RowsBoundingBoxesMapType,
    TableBlockType,
} from '../types';
import { getEmptyMapping } from '../components/mappings/utils';

export function getTablesRowsBoundingBoxesMap(tables: TableBlockType[]): RowsBoundingBoxesMapType {
    const rowsBoundingBoxesMap: RowsBoundingBoxesMapType = {};
    tables.forEach((table) => {
        table.rows.forEach((row) => {
            const boundingBox = row.cells[0].boundingBox;
            rowsBoundingBoxesMap[row.id] = { top: boundingBox.top, height: boundingBox.height };
        });
    });

    return rowsBoundingBoxesMap;
}

export function getTablesCellsBoundingBoxesMap(tables: TableBlockType[]): CellsBoundingBoxesMapType {
    const cellsBoundingBoxesMap: CellsBoundingBoxesMapType = {};
    tables.forEach((table) => {
        table.rows.forEach((row) => {
            row.cells.forEach((cell) => {
                cellsBoundingBoxesMap[cell.id] = cell.boundingBox;
            });
        });
    });

    return cellsBoundingBoxesMap;
}

export function getRowMappingsConfidences(initialMappings: RowMappingType[]): RowIdToDescriptorIdConfidenceMapType {
    const confidences: RowIdToDescriptorIdConfidenceMapType = {};
    initialMappings.forEach((mapping) => {
        let rowId = mapping.rowId;
        let descriptorId = mapping.descriptorId;
        confidences[rowId] = {};
        confidences[rowId][descriptorId] = mapping.confidence;

        mapping.alternatives.forEach((alt) => {
            confidences[rowId][alt.descriptorId] = alt.confidence;
        });
    });

    return confidences;
}

export function getMappingsMap(initialMappings: RowMappingType[]): MappingsMapType {
    return initialMappings.reduce((acc, curr) => {
        const mappingId = _.uniqueId('mapping_');
        acc[mappingId] = curr;
        return acc;
    }, {} as MappingsMapType);
}

export function getRowMappings(initialMappings: RowMappingType[]): RowMappingType[] {
    const mappings: RowMappingType[] = CloneUtils.deepClone(initialMappings);
    /**
     * Handles null ID values in mappings to prevent lookup errors.
     *
     * Problem:
     * - Some mappings have null values for rowId or descriptorId
     * - Using null as a key in Maps causes mismatches when looking up:
     *   - Confidence values
     *   - Alternative mappings
     *
     * Solution:
     * - Assigns temporary IDs to replace null values in rowIds and descriptorIds
     * - This prevents Map lookup errors and related bugs
     */
    mappings.forEach((mapping) => {
        mapping.rowId ??= _.uniqueId(TEMPORARY_ROW_ID_PREFIX);
        mapping.descriptorId ??= _.uniqueId(TEMPORARY_DESCRIPTOR_ID_PREFIX);
    });
    /**
     * Adds the initial mapping as an alternative to fix hover functionality.
     *
     * Background:
     * - When a mapping has alternatives, an "I" icon appears in the UI
     * - Hovering over this icon shows all alternative mappings
     *
     * Problem:
     * After reordering mappings, the hover functionality only shows alternatives
     * from the alternatives array, excluding the initial mapping.
     *
     * Solution:
     * 1. Store the initial mapping as an alternative with type "INITIAL"
     * 2. When determining whether to show the "I" icon, filter out the "INITIAL" alternative
     * 3. This ensures the hover functionality shows all relevant mappings while maintaining
     *    correct UI behavior
     */
    mappings.forEach((mapping) => {
        mapping.alternatives.push({
            confidence: mapping.confidence,
            descriptorId: mapping.descriptorId,
            target: ROW_MAPPING_TARGET.SELF,
            type: ROW_MAPPING_TYPE.INITIAL,
        });
    });

    mappings.push(getEmptyMapping());
    return mappings;
}

type OptionObJType = {
    key: any;
    value: any;
};

// function used to get the options for a select field
export function getOptions<T extends OptionObJType | string | number>(options: T[]) {
    return options.map((o) => ({
        key: (o as OptionObJType).key ?? o,
        text: (o as OptionObJType).value ?? o,
        value: (o as OptionObJType).key ?? o,
    }));
}
